import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { LoginCredentials } from '@app/core/models/login.model';
import { PortalLoginResponse } from '@app/core/models/portal-login.model';
import { DashboardSessionKind, ProviderPortalKind } from '@app/core/models/portal-session.model';
import { getUserDevice } from '@app/core/utils/helpers';
import { Router } from '@angular/router';
import { CONSTANT_APIS } from '@app/shared/constant';

/** Legacy taxi-only token keys (migrated into {@link AuthService.TOKEN_NAME}). */
const LEGACY_TAXI_TOKEN_KEY = 'LYCHEE_TAXI_OFFICE_TOKEN';
const LEGACY_TAXI_PROVIDER_KEY = 'LYCHEE_TAXI_OFFICE_PROVIDER';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  static LOGIN_URL = 'auth/v1/lytchee-work-station/store/login';
  static PORTAL_LOGIN_URL = CONSTANT_APIS.PORTAL_LOGIN;
  /** Single bearer for all portal sessions (store workstation + provider web). */
  static TOKEN_NAME = 'LYCHEE_STORE_TOKEN';
  static USER_ID = 'LYCHEE_USER_ID';
  static USER_TYPE = 'LYCHEE_USER_TYPE';
  static USER_NAME = 'USER_NAME';
  static Store = 'store';
  static CountryCode = 'countryCode';
  static LOGOUT_URL = 'crm/users/auth/logout';
  static TOKEN_USERNAME = 'STORE_USERNAME';

  static SESSION_KIND_KEY = 'LYCHEE_SESSION_KIND';
  static PROVIDER_CONTEXT_KEY = 'LYCHEE_PROVIDER_CONTEXT_JSON';
  static PROVIDER_PORTAL_KEY = 'LYCHEE_PROVIDER_PORTAL_KIND';

  isLoggedIn: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(private readonly http: HttpClient, public router: Router) {
    this.migrateLegacyTaxiTokenIntoPrimarySlot();
    this.isLoggedIn.next(!!this.getToken());
  }

  /** Primary API access token (store or provider web). */
  public getToken(): string | null {
    this.migrateLegacyTaxiTokenIntoPrimarySlot();
    return localStorage.getItem(AuthService.TOKEN_NAME);
  }

  public getUserId(): string | null {
    return localStorage.getItem(AuthService.USER_ID);
  }

  public getUserType(): string | null {
    return localStorage.getItem(AuthService.USER_TYPE);
  }

  public getUserName(): string | null {
    return localStorage.getItem(AuthService.USER_NAME);
  }

  public setToken(token: string): void {
    localStorage.setItem(AuthService.TOKEN_NAME, token);
    this.isLoggedIn.next(true);
  }

  public setUserName(userName: string): void {
    localStorage.setItem(AuthService.USER_NAME, userName);
  }

  public setUserId(userId: number): void {
    localStorage.setItem(AuthService.USER_ID, String(userId));
  }

  public setStore(store: unknown): void {
    localStorage.setItem(AuthService.Store, typeof store === 'string' ? (store as string) : JSON.stringify(store));
  }

  public getStore(): { id?: number; [key: string]: unknown } | null {
    const raw = localStorage.getItem(AuthService.Store);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as { id?: number; [key: string]: unknown };
    } catch {
      return null;
    }
  }

  /** Persist ISO country code for store UI filters (separate from store JSON). */
  public setCountryCode(countryCode: string): void {
    localStorage.setItem(AuthService.CountryCode, countryCode);
  }

  public setCreateInvitation(token: string, userId: number, store: unknown, countryCode: unknown): void {
    this.clearProviderSessionArtifacts();
    localStorage.setItem(AuthService.TOKEN_NAME, token);
    localStorage.setItem(AuthService.USER_ID, String(userId));
    localStorage.setItem(AuthService.Store, JSON.stringify(store));
    localStorage.setItem(AuthService.CountryCode, JSON.stringify(countryCode));
    localStorage.setItem(AuthService.SESSION_KIND_KEY, 'STORE');
    this.clearLegacyTaxiKeysOnly();
    this.isLoggedIn.next(true);
  }

  public setUserType(uType: string): void {
    localStorage.setItem(AuthService.USER_TYPE, String(uType));
  }

  public getSessionKind(): DashboardSessionKind | null {
    this.migrateLegacyTaxiTokenIntoPrimarySlot();
    const explicit = localStorage.getItem(AuthService.SESSION_KIND_KEY);
    if (explicit === 'STORE' || explicit === 'PROVIDER') {
      return explicit;
    }
    if (this.getToken() && this.getStore()) {
      return 'STORE';
    }
    if (this.getToken() && this.getProviderContext()) {
      return 'PROVIDER';
    }
    return null;
  }

  public getProviderPortalKind(): ProviderPortalKind | null {
    const p = localStorage.getItem(AuthService.PROVIDER_PORTAL_KEY);
    return p === 'TAXI_OFFICE' || p === 'TRANSIT_OPERATOR' ? p : null;
  }

  public getProviderContext(): Record<string, unknown> | null {
    const raw = localStorage.getItem(AuthService.PROVIDER_CONTEXT_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  public isStoreSession(): boolean {
    return this.getSessionKind() === 'STORE';
  }

  public isProviderSession(): boolean {
    return this.getSessionKind() === 'PROVIDER';
  }

  public isTaxiOfficeSession(): boolean {
    return this.isProviderSession() && this.getProviderPortalKind() === 'TAXI_OFFICE';
  }

  /** Bus operator office (bus fare collection). */
  public isTransitOperatorSession(): boolean {
    return this.isProviderSession() && this.getProviderPortalKind() === 'TRANSIT_OPERATOR';
  }

  /** Landing page of the signed-in provider portal. */
  public providerHomeUrl(): string[] {
    return this.getProviderPortalKind() === 'TRANSIT_OPERATOR' ? ['/transit/dashboard'] : ['/taxi/overview'];
  }

  /**
   * Store workstation login (existing Lychee Store users).
   * Caller should persist ancillary keys (e.g. countryCode with expiry) after this returns.
   */
  public applyStoreWorkstationLoginResponse(res: {
    authToken: string;
    userName?: string;
    storeUserName?: string;
    device: { userId: number };
    store?: unknown;
  }): void {
    this.clearProviderSessionArtifacts();
    this.setToken(res.authToken);
    const displayName = res.storeUserName ?? res.userName ?? '';
    if (displayName) {
      this.setUserName(displayName);
    }
    this.setUserId(res.device.userId);
    if (res.store) {
      this.setStore(res.store);
    }
    localStorage.setItem(AuthService.SESSION_KIND_KEY, 'STORE');
    this.clearLegacyTaxiKeysOnly();
    this.isLoggedIn.next(true);
  }

  /**
   * Unified portal login: store/merchant or service provider (e.g. taxi office).
   */
  public loginPortal(email: string, password: string): Observable<PortalLoginResponse> {
    const requestBody = {
      email,
      password,
      ...getUserDevice(),
    };
    const headers = new HttpHeaders()
      .set('Accept', 'application/json')
      .set('Accept-Language', 'en-US');
    return this.http.post<PortalLoginResponse>(AuthService.PORTAL_LOGIN_URL, requestBody, { headers });
  }

  public applyPortalLoginResponse(res: PortalLoginResponse): DashboardSessionKind {
    if (res.sessionKind === 'PROVIDER') {
      this.applyProviderLoginResponse(
        {
          authToken: res.authToken,
          provider: res.provider,
        },
        res.providerPortalKind === 'TRANSIT_OPERATOR' ? 'TRANSIT_OPERATOR' : 'TAXI_OFFICE'
      );
      return 'PROVIDER';
    }
    this.applyStoreWorkstationLoginResponse({
      authToken: res.authToken,
      storeUserName: res.storeUserName,
      device: res.device ?? { userId: 0 },
      store: res.store,
    });
    return 'STORE';
  }

  public loginTaxiOffice(email: string, password: string): Observable<unknown> {
    const body = {
      email,
      password,
      ...getUserDevice(),
    };
    const headers = new HttpHeaders().set('Accept', 'application/json').set('Accept-Language', 'en-US');
    return this.http.post(CONSTANT_APIS.TAXI_AUTH_LOGIN, body, { headers }).pipe(
      tap((res: any) => {
        if (res && res.authToken) {
          this.applyTaxiOfficeLoginResponse(res);
        }
      })
    );
  }

  public applyTaxiOfficeLoginResponse(res: { authToken: string; provider?: Record<string, unknown> }): void {
    this.applyProviderLoginResponse(res, 'TAXI_OFFICE');
  }

  public applyProviderLoginResponse(
    res: { authToken: string; provider?: Record<string, unknown> },
    portalKind: ProviderPortalKind
  ): void {
    this.clearStoreSessionArtifacts();
    this.setToken(res.authToken);
    localStorage.setItem(AuthService.SESSION_KIND_KEY, 'PROVIDER');
    localStorage.setItem(AuthService.PROVIDER_PORTAL_KEY, portalKind);
    if (res.provider) {
      localStorage.setItem(AuthService.PROVIDER_CONTEXT_KEY, JSON.stringify(res.provider));
    } else {
      localStorage.removeItem(AuthService.PROVIDER_CONTEXT_KEY);
    }
    this.clearLegacyTaxiKeysOnly();
    this.isLoggedIn.next(true);
  }

  logout(): Observable<boolean> {
    return this.removeLocalStorageItems();
  }

  public removeLocalStorageItemsWithoutLogin(): void {
    this.clearAllPortalKeys();
    this.isLoggedIn.next(false);
  }

  login(loginCredentials: LoginCredentials): Observable<unknown> {
    const requestBody = {
      ...loginCredentials,
      ...getUserDevice(),
    };
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('Accept', 'application/json');
    headers = headers.append('Accept-Language', 'en-US');
    return this.http.post(AuthService.LOGIN_URL, requestBody, { headers });
  }

  /** Unified forgot password (merchant store or service provider). */
  public forgotPortalPassword(email: string): Observable<unknown> {
    const requestBody = {
      email,
      targetPageUrl: `${window.location.origin}/auth/reset-password/`,
    };
    return this.http.post(CONSTANT_APIS.PORTAL_FORGET_PASSWORD, requestBody);
  }

  /** Unified set-new-password step from email link. */
  public resetPortalPassword(code: unknown, id: unknown, body: { newPassword: string }): Observable<unknown> {
    const path = `${CONSTANT_APIS.PORTAL_RESET_PASSWORD}/${code}/${id}`;
    return this.http.post(path, { password: body.newPassword });
  }

  /** @deprecated Use {@link forgotPortalPassword}. */
  public forgotPassword(email: string): Observable<unknown> {
    return this.forgotPortalPassword(email);
  }

  /** @deprecated Use {@link forgotPortalPassword}. */
  public forgotProviderPassword(email: string): Observable<unknown> {
    return this.forgotPortalPassword(email);
  }

  /** @deprecated Use {@link resetPortalPassword}. */
  public resetPassword(code: unknown, id: unknown, body: { newPassword: string }): Observable<unknown> {
    return this.resetPortalPassword(code, id, body);
  }

  /** @deprecated Use {@link resetPortalPassword}. */
  public resetProviderPassword(code: unknown, id: unknown, body: { newPassword: string }): Observable<unknown> {
    return this.resetPortalPassword(code, id, body);
  }

  public navigateToLoginForCurrentPortalOrDefault(): void {
    void this.router.navigate(['/auth/login']);
  }

  /** Invitation / deep-link flows that only receive a store bearer token. */
  public bootstrapStoreSessionFromInvitationToken(token: string): void {
    this.clearProviderSessionArtifacts();
    this.setToken(token);
    localStorage.setItem(AuthService.SESSION_KIND_KEY, 'STORE');
    this.clearLegacyTaxiKeysOnly();
  }

  private removeLocalStorageItems(): Observable<boolean> {
    this.clearAllPortalKeys();
    this.isLoggedIn.next(false);
    setTimeout(() => {
      void this.router.navigate(['/auth/login']);
    }, 200);
    return of(true);
  }

  private clearStoreSessionArtifacts(): void {
    localStorage.removeItem(AuthService.USER_ID);
    localStorage.removeItem(AuthService.USER_NAME);
    localStorage.removeItem(AuthService.USER_TYPE);
    localStorage.removeItem(AuthService.Store);
    localStorage.removeItem(AuthService.TOKEN_USERNAME);
    localStorage.removeItem(AuthService.CountryCode);
    localStorage.removeItem('store');
    localStorage.removeItem('countryCode');
  }

  private clearProviderSessionArtifacts(): void {
    localStorage.removeItem(AuthService.PROVIDER_CONTEXT_KEY);
    localStorage.removeItem(AuthService.PROVIDER_PORTAL_KEY);
    localStorage.removeItem(AuthService.SESSION_KIND_KEY);
  }

  private clearLegacyTaxiKeysOnly(): void {
    localStorage.removeItem(LEGACY_TAXI_TOKEN_KEY);
    localStorage.removeItem(LEGACY_TAXI_PROVIDER_KEY);
  }

  private clearAllPortalKeys(): void {
    localStorage.removeItem(AuthService.TOKEN_NAME);
    this.clearStoreSessionArtifacts();
    this.clearProviderSessionArtifacts();
    this.clearLegacyTaxiKeysOnly();
  }

  private migrateLegacyTaxiTokenIntoPrimarySlot(): void {
    const primary = localStorage.getItem(AuthService.TOKEN_NAME);
    if (primary) {
      return;
    }
    const legacy = localStorage.getItem(LEGACY_TAXI_TOKEN_KEY);
    if (!legacy) {
      return;
    }
    localStorage.setItem(AuthService.TOKEN_NAME, legacy);
    localStorage.removeItem(LEGACY_TAXI_TOKEN_KEY);
    if (!localStorage.getItem(AuthService.SESSION_KIND_KEY)) {
      localStorage.setItem(AuthService.SESSION_KIND_KEY, 'PROVIDER');
      localStorage.setItem(AuthService.PROVIDER_PORTAL_KEY, 'TAXI_OFFICE');
    }
    const legacyProv = localStorage.getItem(LEGACY_TAXI_PROVIDER_KEY);
    if (legacyProv && !localStorage.getItem(AuthService.PROVIDER_CONTEXT_KEY)) {
      localStorage.setItem(AuthService.PROVIDER_CONTEXT_KEY, legacyProv);
      localStorage.removeItem(LEGACY_TAXI_PROVIDER_KEY);
    }
  }
}
